import type { TreeNode } from '../render/tree';
import type { DemoKey } from '../types';

/**
 * Per-die conditions: each die independently meets/fails the condition with a
 * fixed probability out of 6 faces.
 */
export type PerDieCondition = 'even' | 'odd' | 'lt4' | 'ge5';

/**
 * Two-dice-sum conditions: probability depends on the first die's face
 * (`conditionalSumProbability`), since the second die's outcome that
 * satisfies the condition depends on what the first die rolled.
 */
export type SumCondition = 'sum-lt8' | 'sum-eq7' | 'doubles';

export type DiceCondition = PerDieCondition | SumCondition;

const SUM_CONDITIONS: readonly SumCondition[] = ['sum-lt8', 'sum-eq7', 'doubles'];

export function isSumCondition(c: DiceCondition): c is SumCondition {
  return (SUM_CONDITIONS as readonly string[]).includes(c);
}

/** Probability a single die (faces 1–6) meets a per-die condition. */
export function perDieProbability(c: PerDieCondition): number {
  switch (c) {
    case 'even': // faces {2,4,6}
    case 'odd': // faces {1,3,5}
    case 'lt4': // faces {1,2,3}
      return 3 / 6;
    case 'ge5': // faces {5,6}
      return 2 / 6;
  }
}

/**
 * Probability the SECOND die satisfies `c`, given the FIRST die showed
 * `die1` (1–6).
 */
export function conditionalSumProbability(c: SumCondition, die1: number): number {
  switch (c) {
    case 'sum-lt8':
      // die2 < 8 - die1, and die2 is in [1,6] → count of {1..6} below (8-die1).
      return Math.min(6, Math.max(0, 7 - die1)) / 6;
    case 'sum-eq7':
      // Exactly one die2 face (7 - die1) makes the sum 7.
      return 1 / 6;
    case 'doubles':
      // Exactly one die2 face (die1 itself) makes a double.
      return 1 / 6;
  }
}

function coinNode(labelKey: DemoKey, remaining: number): TreeNode<DemoKey> {
  if (remaining <= 0) return { labelKey };
  return {
    labelKey,
    children: [
      { probability: 0.5, node: coinNode('tree.coin.heads', remaining - 1) },
      { probability: 0.5, node: coinNode('tree.coin.tails', remaining - 1) },
    ],
  };
}

/** A coin-toss tree of depth `tosses` (1..4), H/T at 0.5 each level. */
export function buildCoinTree(tosses: number): TreeNode<DemoKey> {
  return coinNode('tree.coin.start', tosses);
}

function perDieNode(labelKey: DemoKey, c: PerDieCondition, remaining: number): TreeNode<DemoKey> {
  if (remaining <= 0) return { labelKey };
  const p = perDieProbability(c);
  return {
    labelKey,
    children: [
      { probability: p, node: perDieNode('tree.cond.met', c, remaining - 1) },
      { probability: 1 - p, node: perDieNode('tree.cond.not', c, remaining - 1) },
    ],
  };
}

/** A `dice`-deep tree where each level branches met/not on a per-die condition. */
export function buildPerDieTree(dice: number, c: PerDieCondition): TreeNode<DemoKey> {
  return perDieNode('tree.dice.start', c, dice);
}

/** `tree.die.1` .. `tree.die.6`, indexed by face (1-based) — avoids a template-literal cast. */
const DIE_FACE_KEYS: readonly DemoKey[] = [
  'tree.die.1',
  'tree.die.2',
  'tree.die.3',
  'tree.die.4',
  'tree.die.5',
  'tree.die.6',
];

function sumDieNode(k: number, c: SumCondition): TreeNode<DemoKey> {
  const p = conditionalSumProbability(c, k);
  return {
    labelKey: DIE_FACE_KEYS[k - 1]!,
    children: [
      { probability: p, node: { labelKey: 'tree.cond.met' } },
      { probability: 1 - p, node: { labelKey: 'tree.cond.not' } },
    ],
  };
}

/** A two-dice-sum tree: six first-die branches (1/6 each), each met/not below. */
export function buildSumTree(c: SumCondition): TreeNode<DemoKey> {
  const children = [];
  for (let k = 1; k <= 6; k++) {
    children.push({ probability: 1 / 6, node: sumDieNode(k, c) });
  }
  return { labelKey: 'tree.dice.start', children };
}

/** Exact fraction behind `conditionProbability` — numerator/denominator in
 * lowest terms NOT required; per-die: n^dice / 6^dice, sum: count / 36. */
export function conditionFraction(dice: number, c: DiceCondition): { num: number; den: number } {
  if (isSumCondition(c)) {
    let num = 0;
    for (let k = 1; k <= 6; k++) num += Math.round(6 * conditionalSumProbability(c, k));
    return { num, den: 36 };
  }
  const perDie = c === 'ge5' ? 2 : 3;
  return { num: perDie ** dice, den: 6 ** dice };
}

/** Overall P(condition) for `dice` dice under condition `c`, as `num / den`
 * from {@link conditionFraction} so the two can't drift. */
export function conditionProbability(dice: number, c: DiceCondition): number {
  const { num, den } = conditionFraction(dice, c);
  return num / den;
}

/** `tree.custom.A` .. `tree.custom.E` — the custom preset's repeated-event
 * outcome labels, indexed by position (0..4). */
export const CUSTOM_EVENT_KEYS: readonly DemoKey[] = [
  'tree.custom.A',
  'tree.custom.B',
  'tree.custom.C',
  'tree.custom.D',
  'tree.custom.E',
];

/** Append one event: it gets 1/n; existing probabilities keep their ratios
 * and split the remaining (n-1)/n. */
export function addEvent(probs: readonly number[]): number[] {
  const n = probs.length + 1;
  return [...probs.map((p) => p * (1 - 1 / n)), 1 / n];
}

/** Drop the last event and renormalize the rest to sum 1 (uniform fallback
 * when the rest sums to 0). */
export function removeEvent(probs: readonly number[]): number[] {
  const rest = probs.slice(0, -1);
  const sum = rest.reduce((s, p) => s + p, 0);
  return sum > 0 ? rest.map((p) => p / sum) : rest.map(() => 1 / rest.length);
}

/** Step `probs` to `count` entries via addEvent/removeEvent. */
export function resizeEvents(probs: readonly number[], count: number): number[] {
  let out = [...probs];
  while (out.length < count) out = addEvent(out);
  while (out.length > count) out = removeEvent(out);
  return out;
}

/** The custom preset's derived tree: one event with `probs.length` outcomes
 * (labels CUSTOM_EVENT_KEYS), repeated `levels` times — every internal node
 * carries the same child distribution. */
export function buildCustomTree(probs: readonly number[], levels: number): TreeNode<DemoKey> {
  function node(labelKey: DemoKey, depth: number): TreeNode<DemoKey> {
    if (depth >= levels) return { labelKey };
    return {
      labelKey,
      children: probs.map((p, i) => ({
        probability: p,
        node: node(CUSTOM_EVENT_KEYS[i]!, depth + 1),
      })),
    };
  }
  return node('tree.custom.start', 0);
}
